import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AleaSelectedTag from './AleaSelectedTag';

interface FilterOption {
  label: string;
  value: string;
  id: string;
}

interface Filters {
  rstate?: FilterOption[];
  station_id?: FilterOption[];
  features?: FilterOption[];
  has_alea_set?: FilterOption[];
  carehome_id?: FilterOption[];
}

interface AleaSelectedTagsProps {
  filters?: Filters;
}

const AleaSelectedTags: React.FC<AleaSelectedTagsProps> = ({ filters }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  
  // Check if any filters are applied
  const hasActiveFilters = ['rstate', 'station_id', 'features', 'carehome_id', 'has_alea_set'].some(key => 
    params.has(key)
  );
  
  if (!hasActiveFilters) {
    return null;
  }
  
  /**
   * Get category label based on filter key
   */
  const getCategoryLabel = (key: string): string => {
    switch (key) {
      case 'rstate':
        return t('status');
      case 'station_id':
        return t('livingArea');
      case 'features':
        return t('features');
      case 'carehome_id':
        return t('careHome');
      case 'has_alea_set':
        return t('aleaSet');
      default:
        return key;
    }
  };
  
  /**
   * Get all selected filter options from URL
   */
  const getSelectedFilters = () => {
    const selectedFilters: { key: string; categoryLabel: string; valueLabel: string; id: string }[] = [];
    
    // Process each filter category
    Object.entries(filters || {}).forEach(([key, options]) => {
      const paramValues = params.get(key)?.split(',') || [];
      
      // Find matching options for this filter category
      paramValues.forEach(value => {
        const option = options?.find((opt: FilterOption) => opt.id === value);
        if (option) {
          selectedFilters.push({
            key,
            categoryLabel: getCategoryLabel(key),
            valueLabel: key === 'rstate' ? t(option.label) : option.label,
            id: option.id
          });
        }
      });
    });
    
    return selectedFilters;
  };
  
  /**
   * Handle removing a filter tag
   */
  const handleRemoveFilter = (filterId: string, filterKey: string) => {
    const values = params.get(filterKey)?.split(',') || [];
    const newValues = values.filter(v => v !== filterId);
    
    if (newValues.length) {
      params.set(filterKey, newValues.join(','));
    } else {
      params.delete(filterKey);
    }
    
    // Reset to page 1 when removing filters
    params.set('page', '1');
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };
  
  const selectedFilters = getSelectedFilters();
  
  if (selectedFilters.length === 0) {
    return null;
  }
  
  return (
    <Box mb="17px">
      <Flex flexWrap="wrap" alignItems="center">
        {selectedFilters.map((filter) => (
          <AleaSelectedTag
            key={`${filter.key}-${filter.id}`}
            label={`${filter.categoryLabel}: ${filter.valueLabel}`}
            onRemove={() => handleRemoveFilter(filter.id, filter.key)}
          />
        ))}
      </Flex>
    </Box>
  );
};

export default AleaSelectedTags; 